// Roles.jsx (cards + phosphor-react + revised components)
import React, { Fragment, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { DeleteModal, Loading, TextInput } from "../../components"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import {
  DotsThreeOutlineVertical,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Plus,
} from "phosphor-react"

function Roles() {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [roles, setRoles] = useState([])
  const [roleId, setRoleId] = useState(null)
  const [done, setDone] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  const [q, setQ] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const rolesPerPage = 12

  useEffect(() => {
    getRoles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done])

  const getRoles = async () => {
    try {
      setErrorMessage("")
      setLoading(true)
      const response = await axiosInstance.get("/role/search")
      setRoles(response?.data?.data || [])
    } catch (error) {
      const msg = error?.response?.data?.message || "Role fetch failed"
      setErrorMessage(msg)
      toast.error(msg)
      console.error("Role fetch failed:", error?.response?.data || error)
    } finally {
      setLoading(false)
    }
  }

  const filteredRoles = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return roles

    return (roles || []).filter((role) => {
      const name = String(role?.name || "").toLowerCase()
      const desc = String(role?.description || "").toLowerCase()
      return name.includes(term) || desc.includes(term)
    })
  }, [roles, q])

  const pagesCount = Math.ceil(filteredRoles.length / rolesPerPage) || 1
  const safePage = Math.min(Math.max(currentPage, 1), pagesCount)

  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesCount])

  const indexOfLastRole = safePage * rolesPerPage
  const indexOfFirstRole = indexOfLastRole - rolesPerPage
  const currentRoles = filteredRoles.slice(indexOfFirstRole, indexOfLastRole)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full h-full px-6 py-5 bg-neutral-50/80 dark:bg-neutral-950/40"
    >
      {loading && <Loading />}

      <div className="w-full space-y-4 h-full">
        {/* Header */}
        <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row gap-4">
          <div className="text-start">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Roles</h2>
              <span className="px-3 py-1 text-xs rounded-full text-cyan-600 bg-cyan-100 dark:bg-cyan-600/20 dark:text-cyan-400">
                {filteredRoles.length} {filteredRoles.length === 1 ? "Role" : "Roles"}
              </span>
            </div>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Manage user roles and permissions.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full lg:w-auto">
            <div className="w-full lg:w-80">
              <TextInput
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search roles..."
                icon={MagnifyingGlass}
              />
            </div>

            <Link
              to="/Roles/new"
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-lg hover:bg-cyan-400 focus:outline-none"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Role
            </Link>
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
            {errorMessage}
          </div>
        ) : null}

        {/* Cards */}
        <div className="flex-1 ">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {currentRoles.map((role) => (
              <div
                key={role.id}
                className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 p-4 hover:shadow-sm transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 text-start">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                      {role.name || "—"}
                    </div>
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-3">
                      {role.description || "—"}
                    </div>
                  </div>

                  <Menu as="div" className="relative">
                    <MenuButton className="inline-flex items-center p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 focus:outline-none">
                      <DotsThreeOutlineVertical size={18} />
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 mt-2 w-48 rounded-2xl shadow-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 focus:outline-none overflow-hidden z-50">
                        <MenuItem>
                          {({ active }) => (
                            <Link
                              to={`/Roles/update/${role.id}`}
                              className={[
                                "flex items-center gap-2 px-4 py-2 text-sm",
                                active ? "bg-neutral-100 dark:bg-neutral-800" : "",
                                "text-neutral-700 dark:text-neutral-200",
                              ].join(" ")}
                            >
                              <PencilSimple size={16} />
                              Edit
                            </Link>
                          )}
                        </MenuItem>

                        <MenuItem>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => {
                                setRoleId(role.id)
                                setDeleteModal(true)
                              }}
                              className={[
                                "w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600",
                                active ? "bg-neutral-100 dark:bg-neutral-800" : "",
                              ].join(" ")}
                            >
                              <Trash size={16} />
                              Delete
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              </div>
            ))}
          </div>

          {!currentRoles.length && (
            <div className="mt-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No roles found.
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredRoles.length > rolesPerPage && (
          <div className="mt-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <p className="text-sm text-neutral-700 dark:text-neutral-400">
              Showing {indexOfFirstRole + 1} to {Math.min(indexOfLastRole, filteredRoles.length)} of{" "}
              {filteredRoles.length} roles
            </p>

            <div className="flex flex-wrap gap-2">
              {Array.from({ length: pagesCount }, (_, i) => (
                <button
                  key={i}
                  onClick={() => paginate(i + 1)}
                  className={[
                    "px-3 py-1 text-sm font-medium rounded-xl border",
                    safePage === i + 1
                      ? "bg-[#2B2B35] text-white border-[#2B2B35]"
                      : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-800",
                  ].join(" ")}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <DeleteModal
        setDeleteId={setRoleId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={roleId}
        Model="role"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </motion.div>
  )
}

export default Roles
